import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Star, ShieldCheck, Loader2, ExternalLink, Angry, Frown, Meh, Smile, Laugh } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Logo } from "../components/common";
import { usePageMeta } from "../lib/seo";

function sessionId() {
  let s = sessionStorage.getItem("sv_session");
  if (!s) { s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("sv_session", s); }
  return s;
}

// Face scale for "Rate a few details" — red angry → green laughing.
const FACE_SCALE = [
  { n: 1, Icon: Angry, label: "Terrible", ring: "border-red-500",     fill: "bg-red-500",     text: "text-white", idleBorder: "border-red-300",     idleText: "text-red-400",     halo: "ring-red-200" },
  { n: 2, Icon: Frown, label: "Poor",     ring: "border-orange-500",  fill: "bg-orange-500",  text: "text-white", idleBorder: "border-orange-300",  idleText: "text-orange-400",  halo: "ring-orange-200" },
  { n: 3, Icon: Meh,   label: "Okay",     ring: "border-amber-400",   fill: "bg-amber-400",   text: "text-white", idleBorder: "border-amber-300",   idleText: "text-amber-400",   halo: "ring-amber-200" },
  { n: 4, Icon: Smile, label: "Good",     ring: "border-lime-500",    fill: "bg-lime-500",    text: "text-white", idleBorder: "border-lime-400",    idleText: "text-lime-500",    halo: "ring-lime-200" },
  { n: 5, Icon: Laugh, label: "Amazing",  ring: "border-emerald-500", fill: "bg-emerald-500", text: "text-white", idleBorder: "border-emerald-400", idleText: "text-emerald-500", halo: "ring-emerald-200" },
];

// Persistent, pseudonymous browser/app-installation id (first-party localStorage).
// Survives across visits in this browser; resets if storage is cleared / private mode / different browser.
export function visitorId() {
  try {
    let v = localStorage.getItem("sv_visitor");
    if (!v) { v = "vis_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("sv_visitor", v); }
    return v;
  } catch { return null; }
}

const PROBLEMS = ["Long wait", "Product knowledge", "Billing issue", "Rude service", "Out of stock", "Other"];

const STAR_LABELS = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Very good", 5: "Excellent" };
const STAR_LABEL_COLORS = { 1: "#DC2626", 2: "#EA580C", 3: "#D97706", 4: "#65A30D", 5: "#059669" };
const STAR_OUTLINE = { 1: "#DC2626", 2: "#F97316", 3: "#EAB308", 4: "#84CC16", 5: "#16A34A" };

export default function PublicFeedback() {
  const { token } = useParams();
  usePageMeta("Rate your service | Servantage", "/", true);
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("rating"); // rating | details | done
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [qa, setQa] = useState({});
  const [dims, setDims] = useState({});
  const [comment, setComment] = useState("");
  const [neg, setNeg] = useState({ problem_category: "", resolved_on_site: null, wants_followup: null, contact_name: "", contact_phone: "", contact_email: "", allow_follow_up: false });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [splash, setSplash] = useState(false);
  // Tracks which face just received a tap so we can replay the bounce animation.
  const [tapPulse, setTapPulse] = useState({ key: null, token: 0 });

  const tapFace = useCallback((dmKey, n) => {
    setDims((cur) => (cur[dmKey] === n ? Object.fromEntries(Object.entries(cur).filter(([k]) => k !== dmKey)) : { ...cur, [dmKey]: n }));
    setTapPulse({ key: `${dmKey}-${n}`, token: Date.now() });
    try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15); } catch { /* older iOS Safari ignores it */ }
  }, []);

  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get("s") === "nfc" ? "nfc" : "qr";
    api.get(`/public/r/${token}?v=${encodeURIComponent(visitorId() || "")}&s=${src}`).then((r) => setIdentity(r.data)).catch((e) => {
      setError(e?.response?.data?.detail || "This feedback link is not valid.");
    });
  }, [token]);

  const starRefs = useRef({});
  const fireConfetti = useCallback((originEl) => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    const rect = originEl?.getBoundingClientRect?.();
    const origin = rect
      ? { x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight }
      : { x: 0.5, y: 0.5 };
    const colors = ["#F59E0B", "#F97316", "#EC4899", "#8B5CF6", "#10B981", "#4338CA"];
    confetti({ particleCount: 70, spread: 70, startVelocity: 42, ticks: 180, scalar: 0.95, origin, colors, zIndex: 9999 });
    confetti({ particleCount: 35, spread: 110, startVelocity: 28, ticks: 150, scalar: 0.75, origin, colors, zIndex: 9999 });
  }, []);

  const pickRating = (n) => {
    setRating(n);
    setSplash(true);
    if (n === 5) {
      fireConfetti(starRefs.current[5]);
      try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate([20, 40, 25]); } catch { /* ignore */ }
    }
    setTimeout(() => { setSplash(false); setStep("details"); }, 900);
  };
  const hasDetails = (identity?.waiter_dimensions?.length || identity?.dimensions?.length || 0) +
                     (identity?.store_dimensions?.length || 0) +
                     (identity?.waiter_questions?.length || identity?.questions?.length || 0) +
                     (identity?.store_questions?.length || 0) > 0;

  // Any admin-flagged criterion scoring < 3 also opens the service-recovery pipeline,
  // even if the overall star rating would otherwise be positive. Keep this in sync with
  // the backend trigger in routes_public.py so the UI collects the manager-contact details
  // from the customer BEFORE submit.
  const triggerFired = useMemo(() => {
    const dimList = [...(identity?.waiter_dimensions || identity?.dimensions || []),
                     ...(identity?.store_dimensions || [])];
    return dimList.some((d) => d.recovery_trigger && (dims[d.key] ?? 0) > 0 && dims[d.key] < 3);
  }, [identity, dims]);
  const recoveryMode = rating > 0 && (rating < 4 || triggerFired);

  const submit = useCallback(async (extra = {}) => {
    setBusy(true);
    try {
      const waiterQs = identity?.waiter_questions || identity?.questions || [];
      const storeQs = identity?.store_questions || [];
      const allQs = [...waiterQs, ...storeQs];
      const question_answers = allQs.length
        ? allQs.map((q) => ({ q, value: qa[q] ?? null }))
        : [];
      const { data } = await api.post("/public/feedback", {
        token, rating, touchpoints: {}, question_answers,
        dimension_scores: Object.keys(dims).length ? dims : null,
        comment: comment || null, session_id: sessionId(), visitor_id: visitorId(),
        source: new URLSearchParams(window.location.search).get("s") === "nfc" ? "nfc" : "qr",
        point_token: new URLSearchParams(window.location.search).get("p") || null,
        ...extra,
      });
      setResult(data);
      setStep("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }, [token, rating, comment, identity, qa, dims]);

  if (error) {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <ShieldCheck className="mb-4 h-12 w-12 text-slate-300" />
          <h1 className="font-head text-xl font-bold text-slate-800">{error}</h1>
          <p className="mt-2 text-sm text-slate-500">Please scan a valid Servantage QR code.</p>
        </div>
      </Shell>
    );
  }
  if (!identity) {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-6"><Logo stacked /><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div></Shell>;
  }

  const first = identity.employee_first_name;

  return (
    <Shell>
      {/* Brief acknowledgement after a rating is given — NOT the final thank-you */}
      {splash && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white" data-testid="rating-ack-splash">
          <div className="sv-pop"><CheckCircle2 className="h-24 w-24 text-indigo-500" /></div>
          <h2 className="mt-5 font-head text-3xl font-bold text-slate-900">Got it!</h2>
          <p className="mt-2 text-base text-slate-500">Just a couple of quick questions to go…</p>
          <div className="mt-5 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="h-6 w-6" fill={n <= rating ? "#F59E0B" : "none"} stroke={n <= rating ? "#F59E0B" : "#CBD5E1"} />
            ))}
          </div>
          <div className="mt-10"><Logo stacked /></div>
          <div className="mt-5"><SolutionBy /></div>
        </div>
      )}

      {/* Header identity */}
      <div className="flex flex-col items-center pt-1 text-center" data-testid="employee-identity">
        <div className="flex items-center justify-center gap-2.5" data-testid="employee-company">
          {identity.organization_logo && (
            <img src={identity.organization_logo} alt={identity.organization_name} className="h-14 w-14 rounded-xl border border-slate-100 object-contain shadow-sm" data-testid="company-logo" />
          )}
          <span className="font-head text-lg font-bold text-slate-800">{identity.organization_name}</span>
        </div>
        {identity.location_name && (
          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{identity.location_name}</p>
        )}
        {identity.employee_photo ? (
          <img src={identity.employee_photo} alt={identity.employee_name} className="mt-3 h-16 w-16 rounded-full object-cover ring-4 ring-indigo-50" data-testid="employee-photo" />
        ) : (
          <div className="mt-3 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 font-head text-2xl font-bold text-indigo-700">
            {first?.[0]}
          </div>
        )}
        <h2 className="mt-2 font-head text-lg font-bold text-slate-900" data-testid="employee-name">{identity.employee_name}</h2>
        {identity.employee_title && (
          <p className="text-sm font-semibold text-indigo-700" data-testid="employee-title">{identity.employee_title}</p>
        )}
      </div>

      {step === "rating" && (
        <div className="flex flex-1 flex-col pt-6 sv-rise">
          <h1 className="text-center font-head text-2xl font-bold text-slate-900" data-testid="feedback-question">{identity.question}</h1>
          <div className="mt-6 flex justify-center gap-1" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="flex w-14 flex-col items-center">
              <button data-testid={`star-${n}`} aria-label={`${n} stars, ${STAR_LABELS[n]}`}
                ref={(el) => { starRefs.current[n] = el; }}
                onMouseEnter={() => setHover(n)} onClick={() => pickRating(n)}
                className="relative p-1 transition-transform hover:scale-110 active:scale-95">
                <Star className="h-12 w-12" strokeWidth={2}
                  fill={n <= (hover || rating) ? "#F59E0B" : "none"}
                  stroke={STAR_OUTLINE[n]} data-testid={`star-icon-${n}`} />
                <span className={`pointer-events-none absolute inset-0 flex items-center justify-center pt-1 font-head text-sm font-bold ${n <= (hover || rating) ? "text-white" : ""}`}
                  style={n <= (hover || rating) ? undefined : { color: STAR_OUTLINE[n] }}
                  data-testid={`star-number-${n}`}>{n}</span>
              </button>
              <span className={`mt-1 h-4 whitespace-nowrap text-xs font-bold transition-opacity duration-150 ${n === (hover || rating) ? "opacity-100" : "opacity-0"}`}
                style={{ color: STAR_LABEL_COLORS[n] }} data-testid={`star-label-${n}`} aria-hidden={n !== (hover || rating)}>{STAR_LABELS[n]}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-slate-400">Tap a star to begin · takes 20 seconds</p>
          {new URLSearchParams(window.location.search).get("p") && (
            <Link to={`/p/${new URLSearchParams(window.location.search).get("p")}${new URLSearchParams(window.location.search).get("s") === "nfc" ? "?s=nfc" : ""}`}
              className="mt-3 block text-center text-sm font-semibold text-indigo-600 hover:text-indigo-800" data-testid="pick-someone-else">← Not {identity.employee_first_name}? Choose someone else</Link>
          )}
        </div>
      )}

      {step === "details" && hasDetails && (
        <div className="flex flex-col pt-4 sv-rise" data-testid="details-step">
          <DetailSection
            testId="section-waiter"
            heading={`Rate your server · ${first}`}
            accent="indigo"
            dims={identity.waiter_dimensions || identity.dimensions || []}
            questions={identity.waiter_questions || identity.questions || []}
            dims_state={dims}
            tapPulse={tapPulse}
            tapFace={tapFace}
            qa={qa}
            setQa={setQa}
          />
          <DetailSection
            testId="section-store"
            heading={`Rate the Store${identity.organization_name ? ` — ${identity.organization_name}` : ""}${identity.location_name ? ` · ${identity.location_name}` : ""}`}
            accent="amber"
            dims={identity.store_dimensions || []}
            questions={identity.store_questions || []}
            dims_state={dims}
            tapPulse={tapPulse}
            tapFace={tapFace}
            qa={qa}
            setQa={setQa}
          />
        </div>
      )}

      {step === "details" && rating >= 4 && !triggerFired && (
        <div className={`flex flex-col sv-rise ${hasDetails ? "pt-6" : "flex-1 justify-center"}`}>
          <h2 className="text-center font-head text-lg font-bold text-slate-900">Anything you'd like to add? <span className="text-xs font-medium text-slate-400">(optional)</span></h2>
          <Textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder={`Tell us more about your experience with ${first}…`} className="mt-4 min-h-[120px]" />
          <Button data-testid="feedback-submit" disabled={busy} onClick={() => submit()}
            className="mt-6 h-12 w-full rounded-full bg-[#4338CA] text-base hover:bg-[#3730A3]">
            {busy ? "Submitting…" : "Submit feedback"}
          </Button>
        </div>
      )}

      {step === "details" && recoveryMode && (
        <div className={`flex flex-col sv-rise ${hasDetails ? "pt-6" : "flex-1 justify-center"}`}>
          <h2 className="text-center font-head text-xl font-bold text-slate-900">
            {rating < 4 ? "Help us make this right" : "One quick thing"}
          </h2>
          <p className="mt-1 text-center text-sm text-slate-500">
            {rating < 4
              ? "The more you share, the faster a manager can resolve it for you."
              : "You rated a key area below our threshold — share your contact details if you would like a manager to follow up."}
          </p>
          <div className="mt-5 space-y-5">
            {rating < 4 && (
              <>
                <div>
                  <p className="mb-2 font-head text-sm font-bold text-slate-900">What went wrong?</p>
                  <div className="flex flex-wrap gap-2">
                    {PROBLEMS.map((p) => (
                      <button key={p} data-testid={`problem-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => setNeg({ ...neg, problem_category: p })}
                        className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          neg.problem_category === p ? "border-[#4338CA] bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}>{p}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 font-head text-sm font-bold text-slate-900">Tell us what happened</p>
                  <Textarea data-testid="negative-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder="A manager will read this directly. Share as much detail as you can — what happened, when, who else was involved."
                    className="min-h-[110px]" />
                </div>
                <div>
                  <p className="mb-2 font-head text-sm font-bold text-slate-900">Was it resolved at the time?</p>
                  <div className="flex gap-2">
                    {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setNeg({ ...neg, resolved_on_site: val })}
                        data-testid={`resolved-${lbl.toLowerCase()}`}
                        className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${
                          neg.resolved_on_site === val ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {rating >= 4 && triggerFired && (
              <div>
                <p className="mb-2 font-head text-sm font-bold text-slate-900">
                  Anything you'd like to add? <span className="text-xs font-medium text-slate-400">(optional)</span>
                </p>
                <Textarea data-testid="trigger-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="A manager will read this directly."
                  className="min-h-[100px]" />
              </div>
            )}

            <div>
              <p className="mb-2 font-head text-sm font-bold text-slate-900">Would you like a manager to contact you for resolution?</p>
              <div className="flex gap-2">
                {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                  <button key={lbl} onClick={() => setNeg({ ...neg, wants_followup: val, allow_follow_up: val, ...(val ? {} : { contact_name: "", contact_phone: "", contact_email: "" }) })}
                    data-testid={`wants-followup-${lbl.toLowerCase()}`}
                    className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${
                      neg.wants_followup === val ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}>{lbl}</button>
                ))}
              </div>
            </div>

            {neg.wants_followup === true && (
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 sv-rise" data-testid="followup-form">
                <p className="font-head text-sm font-bold text-slate-900">Your contact details</p>
                <p className="mt-0.5 text-xs text-slate-500">A manager from {identity.organization_name} will reach out personally.</p>
                <div className="mt-3 space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-700">Name <span className="text-rose-500">*</span></label>
                    <Input data-testid="negative-name" value={neg.contact_name} required aria-required="true"
                      onChange={(e) => setNeg({ ...neg, contact_name: e.target.value })}
                      placeholder="Your name" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-700">Mobile number <span className="text-rose-500">*</span></label>
                    <Input data-testid="negative-phone" type="tel" inputMode="tel" value={neg.contact_phone} required aria-required="true"
                      onChange={(e) => setNeg({ ...neg, contact_phone: e.target.value })}
                      placeholder="e.g. 082 123 4567" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-700">Email <span className="text-xs font-normal text-slate-400">(optional)</span></label>
                    <Input data-testid="negative-email" type="email" inputMode="email" value={neg.contact_email}
                      onChange={(e) => setNeg({ ...neg, contact_email: e.target.value })}
                      placeholder="Email address" />
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-slate-500">By tapping Submit, you consent to be contacted about this feedback.</p>
              </div>
            )}

            {neg.wants_followup === false && (
              <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4 text-center sv-rise" data-testid="followup-apology">
                <p className="font-head text-sm font-bold text-slate-900">We're sorry we fell short.</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Your rating still matters. We use every piece of feedback to train our team and address the exact problem areas you've flagged — so the next customer gets a better experience.
                </p>
              </div>
            )}
          </div>
          {(() => {
            const trimmedName = neg.contact_name.trim();
            const trimmedPhone = neg.contact_phone.trim();
            const followupInvalid = neg.wants_followup === true && (!trimmedName || !trimmedPhone);
            return (
              <Button data-testid="negative-submit" disabled={busy || neg.wants_followup === null || followupInvalid}
                onClick={() => {
                  if (followupInvalid) {
                    toast.error("Please enter your name and mobile number so a manager can reach you.");
                    return;
                  }
                  submit({
                    problem_category: neg.problem_category || null,
                    resolved_on_site: neg.resolved_on_site,
                    contact_name: trimmedName || null,
                    contact_phone: trimmedPhone || null,
                    contact_email: neg.contact_email.trim() || null,
                    contact_detail: trimmedPhone || neg.contact_email.trim() || null,
                    allow_follow_up: !!neg.wants_followup,
                  });
                }}
                className="mt-6 h-12 w-full rounded-full bg-[#4338CA] text-base hover:bg-[#3730A3] disabled:opacity-50">
                {busy ? "Submitting…" : "Submit"}
              </Button>
            );
          })()}
        </div>
      )}

      {step === "done" && (
        <div className="flex flex-1 flex-col items-center justify-center text-center sv-rise" data-testid="feedback-done">
          <CheckCircle2 className="mb-4 h-16 w-16 text-emerald-500" />
          <h2 className="font-head text-2xl font-bold text-slate-900">{result?.message || "Thank you!"}</h2>
          <p className="mt-2 text-sm text-slate-500">Your feedback helps improve service.</p>

          {Array.isArray(result?.vouchers) && result.vouchers.map((v) => (
            <div key={v.code} data-testid={`voucher-card-${v.code}`}
              className="mt-6 w-full rounded-2xl border-2 border-dashed border-indigo-300 bg-indigo-50/60 p-5 text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-500">A thank-you from us</div>
              <div className="mt-1 font-head text-lg font-bold text-slate-900">{v.title}</div>
              <div className="mt-0.5 text-sm text-slate-600">{v.offer}</div>
              <div className="mt-3 inline-block rounded-xl bg-white px-4 py-2 font-mono text-2xl font-bold tracking-[0.3em] text-slate-900 shadow-sm" data-testid={`voucher-code-${v.code}`}>{v.code}</div>
              <div className="mt-2 text-[11px] text-slate-500">Show this code in store · Valid until {String(v.expires_at).slice(0, 10)}</div>
              {v.terms && <p className="mt-2 text-[10px] leading-relaxed text-slate-400">{v.terms}</p>}
            </div>
          ))}

          {result?.flow === "positive" && result?.google_review_url && (
            <a href={result.google_review_url} target="_blank" rel="noreferrer" data-testid="google-review-link"
              onClick={() => { api.post(`/public/feedback/${result.feedback_id}/review-click`).catch(() => {}); }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#4338CA] px-6 py-3 text-sm font-bold text-white hover:bg-[#3730A3]">
              Share publicly on Google <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {result?.flow === "positive" && result?.google_review_url && (
            <p className="mt-3 text-xs text-slate-400">Only if you'd like to — it's entirely optional.</p>
          )}
        </div>
      )}
    </Shell>
  );
}

function DetailSection({ testId, heading, accent, dims, questions, dims_state, tapPulse, tapFace, qa, setQa }) {
  const hasAny = (dims?.length || 0) + (questions?.length || 0) > 0;
  if (!hasAny) return null;
  const accentCls = accent === "store"
    ? "border-amber-200 bg-amber-50/40"
    : "border-indigo-200 bg-indigo-50/30";
  const headCls = accent === "store" ? "text-amber-800" : "text-indigo-800";
  return (
    <div className={`mt-5 rounded-2xl border p-4 ${accentCls}`} data-testid={testId}>
      <h3 className={`font-head text-base font-bold ${headCls}`}>{heading}</h3>
      {dims?.length > 0 && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`${testId}-dims`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rate a few details</p>
          {dims.map((dm) => {
            const score = dims_state[dm.key];
            const triggered = dm.recovery_trigger && score > 0 && score < 3;
            return (
            <div key={dm.key} className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 border-t border-slate-100 pt-2 first-of-type:border-t-0 first-of-type:pt-0">
              <span className="basis-[6rem] min-w-[6rem] flex-1 break-normal pr-1 text-sm font-bold text-slate-800" data-testid={`dim-label-${dm.key}`}>{dm.label}</span>
              <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                {FACE_SCALE.map(({ n, Icon, label, ring, fill, text, idleBorder, idleText, halo }) => {
                  const selected = dims_state[dm.key] === n;
                  const pulseKey = `${dm.key}-${n}`;
                  const pulsing = tapPulse.key === pulseKey;
                  const reactKey = pulsing ? `${pulseKey}-${tapPulse.token}` : pulseKey;
                  return (
                    <button key={reactKey} type="button" data-testid={`dim-${dm.key}-${n}`} aria-label={`${dm.label}: ${label}`} title={label}
                      data-pulse={pulsing ? "1" : "0"}
                      onClick={() => tapFace(dm.key, n)}
                      className={`sv-face-tap inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-150 sm:h-10 sm:w-10 ${selected ? `${ring} ${fill} ${text} ${halo} shadow-md ring-2 scale-110` : `bg-white ${idleBorder} ${idleText} hover:scale-105`}`}>
                      <Icon className="h-[18px] w-[18px] sm:h-5 sm:w-5" strokeWidth={selected ? 2.8 : 2} />
                    </button>
                  );
                })}
              </div>
              {triggered && (
                <p className="w-full rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium leading-snug text-rose-700" data-testid={`dim-trigger-note-${dm.key}`}>
                  Your {dm.label} score is below our threshold – Enter your contact details below if you would like a followup
                </p>
              )}
            </div>
            );
          })}
        </div>
      )}
      {(questions || []).map((q) => {
        const cur = qa[q];
        const setVal = (val) => setQa({ ...qa, [q]: val });
        return (
          <div key={q} className="mt-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <p className="font-head text-base font-bold leading-snug text-slate-900" data-testid="question-text">{q}</p>
            <div className="mt-2 flex gap-2">
              {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                <button key={lbl} data-testid={`tp-${String(q).slice(0, 20)}-${lbl.toLowerCase()}`}
                  onClick={() => setVal(val)}
                  className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${
                    cur === val
                      ? (val ? "bg-emerald-500 text-white" : "bg-slate-800 text-white")
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-6 sm:px-6">
        {children}
        <div className="mt-6"><SolutionBy /></div>
        <div className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          Anonymous & secure ·{" "}
          <Link to="/privacy" className="underline hover:text-slate-600" data-testid="privacy-link">Privacy</Link>
        </div>
      </div>
    </div>
  );
}

function SolutionBy() {
  return (
    <p className="text-center text-sm font-bold text-black" data-testid="solution-by-servantage">
      Solution brought to you by <span className="text-black">Ser</span><span className="text-[#4338CA]">vantage</span>
    </p>
  );
}
